import { useEffect, useState, useRef, useCallback } from 'react';
import { SVG } from '@svgdotjs/svg.js';

import useStore from '../../store/store';
import { HeightRuler } from './common/HeightRuler';

const BuildingBarrier = () => {
	const gRef = useRef();
	const gParentRef = useRef();
	const textGroupRef = useRef();
	const barrierRef = useRef();
	const closeBarrierHeightRef = useRef();
	const farBarrierHeightRef = useRef();
	const closeBarrierDistanceRef = useRef();
	const farBarrierDistanceRef = useRef();
	const leftCircleRef = useRef();
	const rightCircleRef = useRef();
	const [leftRulerRef, setLeftRulerRef] = useState({ current: undefined });
	const [rightRulerRef, setRightRulerRef] = useState({ current: undefined });
	const distance = useStore((state) => state.distance);
	const getUnitFromMeter = useStore((state) => state.getUnitFromMeter);
	const getSingleUnit = useStore((state) => state.getSingleUnit);
	const singleUnit = getSingleUnit();
	const setCloseBarrier = useStore((state) => state.setCloseBarrier);
	const setFarBarrier = useStore((state) => state.setFarBarrier);
	const setBarriers = useStore((state) => state.setBarriers);
	const getfirstBarrierInitialPosition = useStore((state) => state.getfirstBarrierInitialPosition);

	const [closeBarrierX, closeBarrierY, farBarrierX, farBarrierY] = useStore((state) => (
		Number(state.barrier1.distance) <= Number(state.barrier2.distance)
			? [state.barrier1.distance, state.barrier1.height, state.barrier2.distance, state.barrier2.height]
			: [state.barrier2.distance, state.barrier2.height, state.barrier1.distance, state.barrier1.height]
	));

	const onHeightChange = useCallback((newHeight) => {
		setBarriers({ height: newHeight }, { height: newHeight });
	}, []);

	useEffect(() => {
		closeBarrierHeightRef.current = closeBarrierY;
		farBarrierHeightRef.current = farBarrierY;
	}, [closeBarrierY, farBarrierY]);

	useEffect(() => {
		closeBarrierDistanceRef.current = closeBarrierX;
		farBarrierDistanceRef.current = farBarrierX;
	}, [closeBarrierX, farBarrierX]);

	useEffect(() => {
		const draw = SVG('#barriers');
		const gParent = gParentRef.current = draw.group().id('building-barrier');
		const { x, y } = getfirstBarrierInitialPosition();

		const g = gRef.current = gParent.group();
		setRightRulerRef({ current: gParent.group().transform({ translateX: farBarrierX * singleUnit / 0.1 }) });
		setLeftRulerRef({ current: gParent.group().transform({ translateX: closeBarrierX * singleUnit / 0.1 }) });
		gParent.transform({ translateX: x, translateY: y });

		barrierRef.current = g.polyline().fill('#ddd').stroke({ width: 3, color: '#005c56', linejoin: 'round' });

		const textGroup = textGroupRef.current = g.group();
		textGroup.transform({
			translateX: getUnitFromMeter(closeBarrierX + Number(farBarrierX) - Number(closeBarrierX)),
			translateY: -getUnitFromMeter(closeBarrierY)
		}).attr({ class: 'draggable' });
		textGroup.circle(6).cx(0).cy(0).fill('#005c56');
		const buildingText = textGroup.text('BUILDING').transform({ translate: { x: 0, y: -15 } }).font({ anchor: 'middle', size: '12px' }).fill('#ffffff')
		const buildingTextSize = buildingText.node.getBBox();
		textGroup.rect(buildingTextSize.width + 10, buildingTextSize.height + 4)
			.transform({ translate: { x: buildingTextSize.x - 5, y: buildingTextSize.y - 17 } })
			.radius(5)
			.fill('#ef7521')
			.backward();
		let boxAtDragStart;
		textGroup
			.draggable()
			.on('dragstart', (e) => {
				boxAtDragStart = e.detail.box;
			})
			.on('dragmove', (e) => {
				e.preventDefault();
				const { box } = e.detail;

				if (Math.abs(boxAtDragStart.y2 - box.y2) > 0) {
					const yOffset = box.y2 - box.y2 % singleUnit;
					const yOffsetInMeters = Number((yOffset * 0.1 / singleUnit).toFixed(1));

					let closeBarrierNewHeight = Number(closeBarrierHeightRef.current) - yOffsetInMeters;
					let farBarrierNewHeight = Number(farBarrierHeightRef.current) - yOffsetInMeters;

					if (closeBarrierNewHeight < 0 && closeBarrierNewHeight <= farBarrierNewHeight) {
						const newOffset = Number(closeBarrierHeightRef.current).toFixed(1);
						closeBarrierNewHeight = 0;
						farBarrierNewHeight = Number(farBarrierHeightRef.current) - newOffset;
					} else if (farBarrierNewHeight < 0) {
						const newOffset = Number(farBarrierHeightRef.current).toFixed(1);
						closeBarrierNewHeight = Number(closeBarrierHeightRef.current) - newOffset;
						farBarrierNewHeight = 0;
					}

					setBarriers({ height: closeBarrierNewHeight }, { height: farBarrierNewHeight });
				}

				if (Math.abs(boxAtDragStart.x2 - box.x2) > 0) {
					const xOffset = box.x2 - box.x2 % singleUnit - box.w / 2;
					const offsetInMeters = Number((xOffset * 0.1 / singleUnit).toFixed(1));

					let closeBarrierNewDistance = Number(closeBarrierDistanceRef.current) + offsetInMeters;
					let farBarrierNewDistance = Number(farBarrierDistanceRef.current) + offsetInMeters;

					if (closeBarrierNewDistance < 0) {
						const newOffset = Number(closeBarrierNewDistance) - offsetInMeters;
						closeBarrierNewDistance = 0;
						farBarrierNewDistance = Number(farBarrierDistanceRef.current) - newOffset;
					} else if (farBarrierNewDistance > distance) {
						const newOffset = Number(farBarrierNewDistance) - distance;
						closeBarrierNewDistance = (Number(closeBarrierNewDistance) - newOffset).toFixed(1);
						farBarrierNewDistance = distance;
					}

					setBarriers({ distance: closeBarrierNewDistance }, { distance: farBarrierNewDistance });
				}
			});

		const leftCircle = leftCircleRef.current = g.group();
		leftCircle.transform({
			translateX: getUnitFromMeter(closeBarrierX),
			translateY: -getUnitFromMeter(closeBarrierY)
		}).attr({ class: 'draggable' });
		leftCircle.rect(16, 16).transform({ translate: { x: -8, y: -8 } }).opacity(0);
		leftCircle.circle(8).cx(0).cy(0).fill('#005c56');
		leftCircle
			.draggable()
			.on('dragmove', (e) => {
				e.preventDefault();
				const { box } = e.detail;
				const newOffset = leftCircle.transform('translateY') + (box.y2 - box.y2 % singleUnit) - box.h / 2;
				setCloseBarrier({ height: newOffset > 0 ? 0 : Math.abs(newOffset * 0.1 / singleUnit).toFixed(1) });
			});

		const rightCircle = rightCircleRef.current = g.group();
		rightCircle.transform({
			translateX: getUnitFromMeter(farBarrierX),
			translateY: -getUnitFromMeter(farBarrierY)
		}).attr({ class: 'draggable' });
		rightCircle.rect(16, 16).transform({ translate: { x: -8, y: -8 } }).opacity(0);
		rightCircle.circle(8).cx(0).cy(0).fill('#005c56');
		rightCircle
			.draggable()
			.on('dragmove', (e) => {
				e.preventDefault();
				const { box } = e.detail;
				const newOffset = rightCircle.transform('translateY') + (box.y2 - box.y2 % singleUnit) - box.h / 2;
				setFarBarrier({ height: newOffset > 0 ? 0 : Math.abs(newOffset * 0.1 / singleUnit).toFixed(1) });
			});

		return () => {
			setRightRulerRef({ current: undefined });
			setLeftRulerRef({ current: undefined });
			gParent.remove();
		};
	}, [distance]);

	useEffect(() => {
		// update line.
		if (barrierRef.current) {
			barrierRef.current.plot(`
				${getUnitFromMeter(closeBarrierX)} 0
				${getUnitFromMeter(closeBarrierX)} -${getUnitFromMeter(closeBarrierY)}
				${getUnitFromMeter(farBarrierX)} -${getUnitFromMeter(farBarrierY)}
				${getUnitFromMeter(farBarrierX)} 0
			`);
		}
		if (textGroupRef.current) {
			const smallBarrier = Number(farBarrierY) > Number(closeBarrierY) ? closeBarrierY : farBarrierY;
			textGroupRef.current.transform({
				translateY: -(Number(smallBarrier) + (Math.abs(farBarrierY - closeBarrierY) / 2)) * singleUnit / 0.1,
				translateX: (farBarrierX - (farBarrierX - closeBarrierX) / 2) * singleUnit / 0.1
			});
		}
		rightRulerRef.current && rightRulerRef.current.transform({
			translateX: farBarrierX * singleUnit / 0.1
		});
		leftRulerRef.current && leftRulerRef.current.transform({
			translateX: closeBarrierX * singleUnit / 0.1
		});
		rightCircleRef.current && rightCircleRef.current.transform({
			translateX: getUnitFromMeter(farBarrierX),
			translateY: -getUnitFromMeter(farBarrierY)
		});
		leftCircleRef.current && leftCircleRef.current.transform({
			translateX: getUnitFromMeter(closeBarrierX),
			translateY: -getUnitFromMeter(closeBarrierY)
		});
	}, [singleUnit, closeBarrierX, closeBarrierY, farBarrierX, farBarrierY]);

	return (
		<>
			{!rightRulerRef.current ? null : (
				<HeightRuler
					gRef={rightRulerRef}
					height={farBarrierY}
					onHeightChange={onHeightChange}
				/>
			)}
			{!leftRulerRef.current ? null : (
				<HeightRuler
					gRef={leftRulerRef}
					height={closeBarrierY}
					onHeightChange={onHeightChange}
					textOnLeft={true}
				/>
			)}
		</>
	);
};

export { BuildingBarrier };